import { copy } from '@ohear/shared'
import { useToast } from '@ohear/ui'
/**
 * "Upload file" — lets a pro actor deliver a DAW-recorded WAV as a take for
 * the current cue instead of performing into the browser mic.
 *
 * Pipeline (mirrors the browser-recording upload chain):
 *   1. parseWavHeader  — reject non-WAVs; surface spec mismatches as
 *      *advisory* (neutral/info, never red) and continue regardless.
 *   2. sha256File      — the presign endpoint wants the content hash.
 *   3. POST presign    — reserves the take row (take_number continues the
 *      cue's current version, computed from local session + server history).
 *   4. PUT WAV binary  — bare fetch to the presigned URL (cross-origin
 *      signed URL: must NOT carry our Authorization header).
 *   5. POST finalize   — flips the row to uploaded + triggers server QC;
 *      the mutation hook invalidates the cue-takes query so TakeHistory
 *      refreshes on its own.
 *
 * All user-facing strings come from copy.upload via useT() — no hardcoded
 * English.
 */
import { type ChangeEvent, useCallback, useRef, useState } from 'react'
import { api } from '../../lib/api'
import {
  type PresignTakeResponse,
  type TakeHistoryEntry,
  useCueTakes,
  useFinalizeTake,
} from '../../lib/api/takes'
import { useT } from '../../lib/i18n/use-t'
import { parseWavHeader, sha256File, wavWarnings } from '../../lib/wav-parse'
import { cx } from './cx'
import { type TakeRecord, useRecordingStore } from './state'

/** Hard cap — sha256File buffers the whole file, and no single take should
 *  come anywhere near this (48 kHz / 24-bit mono ≈ 8.6 MB per minute). */
const MAX_UPLOAD_BYTES = 200 * 1024 * 1024

type Stage = 'idle' | keyof typeof copy.upload.STATUS

const EMPTY_TAKES: readonly TakeRecord[] = []

/**
 * Next take/version for an *uploaded* take. Same semantics as
 * `nextTakeNumber` (continue the cue's current version, bump the take
 * number), but also counts server-side history so a fresh session doesn't
 * collide with takes recorded earlier (presign 409s on duplicates).
 */
function nextUploadNumbers(
  local: readonly TakeRecord[],
  remote: readonly TakeHistoryEntry[],
): { takeNumber: number; versionNumber: number } {
  const versions = [...local.map((t) => t.versionNumber), ...remote.map((t) => t.version_number)]
  if (versions.length === 0) return { takeNumber: 1, versionNumber: 1 }
  const versionNumber = Math.max(...versions)
  const takeNumbers = [
    ...local.filter((t) => t.versionNumber === versionNumber).map((t) => t.takeNumber),
    ...remote.filter((t) => t.version_number === versionNumber).map((t) => t.take_number),
  ]
  const takeNumber = takeNumbers.length === 0 ? 1 : Math.max(...takeNumbers) + 1
  return { takeNumber, versionNumber }
}

export interface UploadTakeButtonProps {
  cueId: string | null
  /** Disable while the recording machine is mid-take (recording/upload/QC). */
  disabled?: boolean
}

export function UploadTakeButton({ cueId, disabled }: UploadTakeButtonProps) {
  const t = useT()
  const toast = useToast()
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [stage, setStage] = useState<Stage>('idle')
  const busy = stage !== 'idle'

  const remoteTakes = useCueTakes(cueId)
  const localTakes = useRecordingStore((s) =>
    cueId ? (s.takesByCue[cueId] ?? EMPTY_TAKES) : EMPTY_TAKES,
  )
  const pushTake = useRecordingStore((s) => s.pushTake)
  const updateTakeUpload = useRecordingStore((s) => s.updateTakeUpload)
  const finalize = useFinalizeTake()

  const handleFile = useCallback(
    async (file: File) => {
      if (!cueId) return
      try {
        // 1. Parse — hard-stop on non-WAV / oversize; advisory on mismatch.
        setStage('parsing')
        const info = await parseWavHeader(file)
        if (!info.valid) {
          toast.show({ title: t(copy.upload.ERRORS.not_wav), severity: 'error', duration: 7000 })
          setStage('idle')
          return
        }
        if (file.size > MAX_UPLOAD_BYTES) {
          toast.show({ title: t(copy.upload.ERRORS.too_large), severity: 'error', duration: 7000 })
          setStage('idle')
          return
        }
        // Advisory only — neutral severity, upload continues. Tone rule:
        // one issue at a time, so surface just the first mismatch.
        const firstWarn = wavWarnings(info)[0]
        if (firstWarn) {
          toast.show({
            title: t(copy.upload.WARNINGS[firstWarn.key](firstWarn.detail)),
            severity: 'info',
            duration: 8000,
          })
        }

        // 2. Hash.
        setStage('hashing')
        const sha256 = await sha256File(file)

        // 3. Presign — real parsed values, not browser-recorder defaults.
        setStage('uploading')
        const { takeNumber, versionNumber } = nextUploadNumbers(
          localTakes,
          remoteTakes.data?.takes ?? [],
        )
        const presigned = await api<PresignTakeResponse>(
          `/api/v1/actor/cues/${cueId}/takes/presign`,
          {
            method: 'POST',
            body: JSON.stringify({
              take_number: takeNumber,
              version_number: versionNumber,
              sha256,
              size_bytes: file.size,
              sample_rate: info.sampleRate,
              bit_depth: info.bitDepth,
              source: 'uploaded',
              original_filename: file.name,
              ...(info.durationSec && info.durationSec > 0
                ? { duration_sec: info.durationSec }
                : {}),
            }),
          },
        )

        // Mirror into the session store so TakeHistory / next-take-number
        // see it immediately (same pattern as useRecordingMachine).
        pushTake({
          takeId: presigned.take_id,
          cueId,
          takeNumber,
          versionNumber,
          recordedAt: Date.now(),
          durationSec: info.durationSec ?? 0,
          peak: 0,
          previewUrl: null,
          uploadStatus: 'uploading',
          uploadProgress: 0,
        })

        // 4. PUT the WAV — bare fetch on purpose: the presigned URL is
        // signature-authenticated; sending our Authorization header would
        // break cross-origin signing.
        const putRes = await fetch(presigned.presigned_put_url, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': 'application/octet-stream' },
        })
        if (!putRes.ok) {
          updateTakeUpload(presigned.take_id, { uploadStatus: 'failed' })
          throw new Error(`Upload failed: ${putRes.status} ${putRes.statusText}`)
        }
        updateTakeUpload(presigned.take_id, { uploadStatus: 'uploaded', uploadProgress: 1 })

        // 5. Finalize — the hook invalidates the cue-takes query on success.
        setStage('finalizing')
        await finalize.mutateAsync({ takeId: presigned.take_id, cueId })

        setStage('done')
        toast.show({ title: t(copy.upload.STATUS.done), severity: 'success' })
      } catch (err) {
        setStage('failed')
        toast.show({
          title: t(copy.upload.ERRORS.upload_failed),
          description: err instanceof Error ? err.message : undefined,
          severity: 'error',
          duration: 8000,
        })
      } finally {
        setStage('idle')
      }
    },
    [cueId, localTakes, remoteTakes.data, pushTake, updateTakeUpload, finalize, t, toast],
  )

  const onPick = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      // Reset so picking the same file again re-fires onChange.
      e.target.value = ''
      if (file) void handleFile(file)
    },
    [handleFile],
  )

  return (
    <div className="flex flex-col items-center gap-1">
      <input
        ref={inputRef}
        type="file"
        accept=".wav,audio/wav,audio/x-wav"
        className="hidden"
        onChange={onPick}
        data-testid="upload-take-input"
      />
      <button
        type="button"
        data-testid="upload-take-button"
        onClick={() => inputRef.current?.click()}
        disabled={busy || disabled || !cueId}
        title={t(copy.upload.BUTTON.upload_file)}
        className={cx(
          'pt-btn',
          'flex flex-col items-center justify-center gap-0.5 px-3 py-2 min-w-[78px]',
        )}
      >
        <span aria-hidden="true" className="text-[15px] leading-none text-pt-text">
          ⬆
        </span>
        <span className="text-[11px] font-semibold leading-none text-pt-text">
          {t(copy.upload.BUTTON.upload_file)}
        </span>
        <span className="pt-kbd mt-0.5">WAV</span>
      </button>
      {stage !== 'idle' && (
        <span
          role="status"
          className="pt-mono text-[10px] uppercase tracking-[0.14em] text-pt-text-soft"
        >
          {t(copy.upload.STATUS[stage])}
        </span>
      )}
    </div>
  )
}
